import React from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { IGame } from '@incentivegames/ig-types/lib/interfaces/apiContracts';
import { dateTimeUtilities } from 'utilities/dateTimeUtilities';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import InfoButton from 'components/InfoButton';
import BonusButton from 'components/BonusButton';

import './GamesSliderView.scss';

export interface IGamesSliderViewData {
  slides: IGame[];
  settings: any;
  currentWeekIndex: number | undefined;
  slideIndex: number | undefined;
  useShadow: boolean;
  showInfoButton: boolean;
  showBonusButton: boolean;
}

interface IGamesSliderViewProps {
  data: IGamesSliderViewData;
}
// TODO use common library
const GamesSliderView = (props: IGamesSliderViewProps) => {
  const { t } = useTranslation();
  // Padding slides used to center align content
  const addPaddingSlides = () => {
    if (props.data.slides.length === 1) return [];
    return [<div key="games-slider__slide-content_-1" className="games-slider__slide-content" />, <div className="games-slider__slide-content" key="games-slider__slide-content_-2" />];
  };

  const renderButtons = () => (
    <div className={`games-slider__button-container ${props.data.useShadow ? 'games-slider__button-container--shadow' : ''}`}>
      {props.data.showInfoButton && <InfoButton />}
      {props.data.showBonusButton && <BonusButton />}
    </div>
  );

  const formatDateRange = (startTime: number, endTime: number) => {
    const formatDate = (dateTime: number): string => {
      const month = t(dayjs(dateTime).format('MMM').toLowerCase());
      const day = dayjs(dateTime).date();
      const ordinal = t(dateTimeUtilities.getNthDay(day));
      if (process.env.REACT_APP_LANGUAGE === 'nl') {
        return `${day} ${month}`;
      }
      return `${month} ${day}${ordinal}`;
    };
    return `${formatDate(startTime)} - ${formatDate(endTime)}`;
  };

  return (
    <div className="games-slider" data-testid="games-slider">
      <div className="games-slider__slider-container">
        {props.data.slides && (
          <Slider
            {...props.data.settings}
            ref={(slickSlider) => {
              slickSlider?.slickGoTo(props.data.slideIndex ?? 0, true);
            }}
          >
            {props.data.slides.map((slide: IGame, index: number) => {
              const { gameName, startTime, endTime } = slide;
              const isFutureGame = (props.data.currentWeekIndex ?? 0) < index;
              const isCurrentGame = props.data.currentWeekIndex === index;
              const dateRange: string = formatDateRange(startTime, endTime);
              return (
                <div className={`games-slider__slide-content ${isFutureGame ? 'games-slider__slide-content--disabled' : ''}`} key={`games-slider__slide-content_${index}`}>
                  <div className="games-slider__game-name">{isCurrentGame ? t('this-round') : gameName}</div>
                  <div className="games-slider__game-dates">{dateRange}</div>
                </div>
              );
            })}
            {addPaddingSlides()}
          </Slider>
        )}
      </div>
      {renderButtons()}
    </div>
  );
};

export default GamesSliderView;
